<template>
  <div class="min-h-screen bg-gray-50">
    <div class="container mx-auto p-2 sm:p-6">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-6">
        <!-- 공지사항 -->
        <div class="lg:col-span-2 space-y-3 sm:space-y-6">
          <div class="bg-white rounded-xl shadow-sm overflow-hidden">
            <div class="flex justify-between items-center p-3 border-b">
              <h2 class="text-[13px] sm:text-[15px] font-semibold">공지사항</h2>
              <router-link to="/notice"
                class="text-blue-500 hover:text-blue-600 text-[10px] sm:text-xs">더보기</router-link>
            </div>
            <div class="divide-y divide-gray-100">
              <!-- 로딩 상태 -->
              <div v-if="isLoading" class="p-6 flex justify-center items-center">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </div>
              <!-- 공지사항 목록 -->
              <template v-else>
                <div v-for="notice in paginatedNotices" :key="notice.id" @click="openDetail(notice)"
                  class="group hover:bg-blue-50 transition-all duration-200 cursor-pointer">
                  <div class="p-3">
                    <div class="flex items-center justify-between">
                      <div class="flex items-center min-w-0 space-x-1">
                        <div v-if="isNew(notice.date)"
                          class="flex-shrink-0 bg-red-500 text-white text-[10px] font-semibold px-1 py-0.5 rounded-full leading-none">
                          NEW
                        </div>
                        <h3
                          class="truncate font-medium text-gray-900 group-hover:text-blue-600 text-[13px] sm:text-[15px]">
                          {{ notice.title }}
                        </h3>
                        <div v-if="notice.has_attachment" class="text-gray-400 flex-shrink-0" @click.stop>
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                            stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                        </div>
                      </div>
                      <span :class="notice.is_read ? 'text-gray-500' : 'text-blue-500'"
                        class="text-[10px] sm:text-xs font-medium whitespace-nowrap ml-2">
                        {{ notice.is_read ? '읽음' : '안읽음' }}
                      </span>
                    </div>
                    <div class="mt-1 flex items-center space-x-3 text-[11px] sm:text-xs text-gray-500">
                      <span class="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {{ notice.admin_name }}
                      </span>
                      <span class="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {{ formatDate(notice.date) }}
                      </span>
                    </div>
                  </div>
                </div>
              </template>
            </div>
            <div class="flex justify-center items-center p-3 border-t">
              <v-pagination v-model="currentNoticePage" :length="totalNoticePages" :total-visible="getTotalVisible"
                density="compact" color="grey-darken-1" class="pagination-text" size="small"
                @update:model-value="handlePageChange"></v-pagination>
            </div>
          </div>
        </div>

        <!-- VMD 요청사항 -->
        <div class="bg-white rounded-xl shadow-sm overflow-hidden self-start">
          <div class="flex justify-between items-center p-3 border-b">
            <h2 class="text-sm sm:text-base font-semibold">VMD 요청사항</h2>
            <router-link to="/vmd/requests"
              class="text-blue-500 hover:text-blue-600 text-[10px] sm:text-xs">더보기</router-link>
          </div>
          <div class="divide-y divide-gray-100">
            <!-- 로딩 상태 -->
            <div v-if="isVmdLoading" class="p-6 flex justify-center items-center">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <!-- VMD 요청사항 목록 -->
            <template v-else>
              <div v-for="request in paginatedVmdRequests" :key="request.id" @click="openVmdDetail(request)"
                class="group hover:bg-blue-50 transition-all duration-200 cursor-pointer">
                <div class="p-3">
                  <div class="flex items-center justify-between">
                    <div class="flex-1">
                      <div class="flex items-center space-x-2">
                        <span :class="getStatusClass(request.status_code)"
                          class="px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium">
                          {{ getStatusText(request.status_code) }}
                        </span>
                        <h3
                          class="text-[13px] sm:text-[15px] font-medium group-hover:text-blue-600 truncate max-w-[200px] sm:max-w-none">
                          {{ request.title }}
                        </h3>
                      </div>
                      <div class="mt-1 flex items-center space-x-3 text-[11px] sm:text-xs text-gray-500">
                        <span class="flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24"
                            stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          {{ request.admin_name }}
                        </span>
                        <span class="flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24"
                            stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {{ formatDate(request.date) }}
                        </span>
                        <span v-if="request.due_date" class="flex items-center text-red-500">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24"
                            stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {{ formatDate_mmdd(request.due_date) }}
                        </span>
                      </div>
                    </div>
                    <div class="flex items-center ">
                      <button v-if="['안읽음', '미제출', '보완요청'].includes(request.status_name)"
                        @click.stop="openVmdDetail(request)"
                        class="inline-flex items-center px-2 py-1 sm:px-3 sm:py-1.5 text-[10px] sm:text-xs font-medium text-white bg-blue-500 rounded-md shadow-sm hover:bg-blue-600 hover:shadow-md transition-all duration-200">
                        제출
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
          <div class="flex justify-center items-center p-3 border-t">
            <v-pagination v-model="currentRequestPage" :length="totalRequestPages" :total-visible="getTotalVisible"
              density="compact" color="grey-darken-1" class="pagination-text" size="small"
              @update:model-value="handleVmdPageChange"></v-pagination>
          </div>
        </div>

        <!-- VMD 제출 목록 -->
        <div class="bg-white rounded-xl shadow-sm overflow-hidden self-start">
          <div class="flex justify-between items-center p-3 border-b">
            <h2 class="text-sm sm:text-base font-semibold">VMD 제출 목록</h2>
            <router-link to="/vmd/submissions"
              class="text-blue-500 hover:text-blue-600 text-[10px] sm:text-xs">더보기</router-link>
          </div>
          <div class="divide-y divide-gray-100">
            <!-- 로딩 상태 -->
            <div v-if="isVmdSubmissionLoading" class="p-6 flex justify-center items-center">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <!-- VMD 제출 목록 -->
            <template v-else>
              <div v-for="submission in paginatedVmdSubmissions" :key="submission.id"
                class="group hover:bg-blue-50 transition-all duration-200 cursor-pointer"
                @click="openVmdSubmissionDetail(submission)">
                <div class="p-3">
                  <div class="flex items-center justify-between">
                    <div class="flex-1">
                      <div class="flex items-center space-x-2">
                        <span :class="getStatusClass(submission.status_code)"
                          class="px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium">
                          {{ getStatusText(submission.status_code) }}
                        </span>
                        <h3
                          class="text-[13px] sm:text-[15px] font-medium group-hover:text-blue-600 truncate max-w-[200px] sm:max-w-none">
                          {{ submission.title }}
                        </h3>
                      </div>
                      <div class="mt-1 flex items-center space-x-3 text-[11px] sm:text-xs text-gray-500">
                        <span class="flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24"
                            stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {{ formatDate(submission.date) }}
                        </span>
                      </div>
                      <!-- 담당자 평가 코멘트가 있는 경우 표시 -->
                      <div v-if="submission.report_result_comment"
                        class="mt-2 relative bg-blue-50 p-2 sm:p-3 rounded-lg border border-blue-100">
                        <!-- 말풍선 꼬리 -->
                        <div
                          class="absolute -top-2 left-5 w-3 h-3 bg-blue-50 border-t border-l border-blue-100 transform rotate-45">
                        </div>
                        <!-- 평가 내용 -->
                        <div class="relative">
                          <div class="flex items-center mb-1">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-blue-500 mr-1.5" fill="none"
                              viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <span class="font-medium text-blue-700 text-[10px] sm:text-[11px] md:text-xs">담당자 평가</span>
                          </div>
                          <p
                            class="text-gray-600 text-[10px] sm:text-[11px] md:text-xs ml-5 truncate max-w-[200px] sm:max-w-[400px] md:max-w-[600px]">
                            {{ submission.report_result_comment }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- 데이터가 없는 경우 -->
              <div v-if="paginatedVmdSubmissions.length === 0"
                class="p-6 text-center text-gray-500 text-xs sm:text-sm md:text-base">
                제출한 VMD가 없습니다.
              </div>
            </template>
          </div>
          <div v-if="totalSubmissionPages > 1" class="flex justify-center items-center p-3 border-t">
            <v-pagination v-model="currentSubmissionPage" :length="totalSubmissionPages"
              :total-visible="getTotalVisible" density="compact" color="grey-darken-1" class="pagination-text"
              size="small" @update:model-value="handleVmdSubmissionPageChange"></v-pagination>
          </div>
        </div>
      </div>
    </div>

    <!-- 상세 보기 사이드 패널 -->
    <transition name="fade">
      <div v-if="selectedNotice" class="fixed inset-0 z-40" @click="closeDetail"></div>
    </transition>
    <transition name="slide">
      <div v-if="selectedNotice"
        class="fixed inset-y-0 right-0 w-full sm:w-[80%] md:w-[70%] lg:w-[60%] xl:w-[50%] 2xl:w-[45%] bg-white shadow-2xl overflow-y-auto z-50">
        <div class="p-3 sm:p-6">
          <div class="flex justify-between items-center mb-2 sm:mb-4">
            <h2 class="text-sm sm:text-base md:text-lg font-bold text-gray-800">{{ selectedNotice.title }}</h2>
            <button @click="closeDetail" class="text-gray-500 hover:text-gray-700">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-4 sm:w-4 md:h-5 md:w-5" fill="none"
                viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="flex items-center space-x-3 sm:space-x-4 mb-3 sm:mb-4">
            <span class="flex items-center text-[11px] sm:text-xs md:text-sm text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 mr-1" fill="none"
                viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {{ formatDate(selectedNotice.date) }}
            </span>
            <span v-if="selectedNotice.admin_name"
              class="flex items-center text-[11px] sm:text-xs md:text-sm text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 mr-1" fill="none"
                viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {{ selectedNotice.admin_name }}
            </span>
          </div>

          <!-- 첨부파일 목록 -->
          <div v-if="selectedNotice.attachments && selectedNotice.attachments.length > 0" class="mb-4 sm:mb-6">
            <h3 class="text-xs sm:text-sm md:text-base font-semibold mb-2 sm:mb-3">첨부파일</h3>
            <div class="space-y-1.5 sm:space-y-2">
              <div v-for="attachment in selectedNotice.attachments" :key="attachment.id"
                @click="downloadAttachment(attachment)"
                class="flex items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200 cursor-pointer">
                <div class="flex items-center space-x-2">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 text-gray-400"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  <span class="text-[11px] sm:text-xs md:text-sm text-gray-700">{{ attachment.original_name }}</span>
                </div>
                <div
                  class="inline-flex items-center px-2 py-1 sm:px-2.5 sm:py-1 md:px-3 md:py-1.5 text-[10px] sm:text-xs font-medium text-blue-600 hover:text-blue-700">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 mr-1"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  다운로드
                </div>
              </div>
            </div>
          </div>

          <div
            class="prose max-w-none text-[12px] sm:text-xs md:text-sm lg:text-base leading-relaxed sm:leading-relaxed"
            v-html="selectedNotice.content"></div>

          <!-- 하단 닫기 버튼 -->
          <div class="sticky bottom-0 left-0 right-0 bg-white border-t p-2.5 sm:p-4 mt-4 sm:mt-6">
            <button @click="closeDetail"
              class="w-full bg-blue-500 text-white py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center justify-center text-[11px] sm:text-xs md:text-sm">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 mr-1.5 sm:mr-2"
                fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              닫기
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- VMD 요청사항 상세 보기 사이드 패널 -->
    <transition name="slide">
      <div v-if="selectedRequest"
        class="fixed inset-y-0 right-0 w-full sm:w-[80%] md:w-[70%] lg:w-[60%] xl:w-[50%] 2xl:w-[45%] bg-white shadow-2xl overflow-y-auto z-50">
        <div class="p-4 sm:p-6">
          <div class="flex justify-between items-center mb-4 sm:mb-6">
            <div class="flex items-center gap-3">
              <h2 class="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-800">{{ selectedRequest.title
              }}
              </h2>
              <span :class="getStatusClass(selectedRequest.status_code)"
                class="px-1.5 py-0.5 rounded-full text-[10px] sm:text-[11px] md:text-xs font-medium">
                {{ getStatusText(selectedRequest.status_code) }}
              </span>
            </div>
            <button @click="selectedRequest = null" class="text-gray-500 hover:text-gray-700">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" fill="none"
                viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="flex items-center space-x-4 mb-4 sm:mb-6">
            <span class="flex items-center text-xs sm:text-sm md:text-base text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-4 sm:w-4 mr-1" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              제출일: {{ formatDate(selectedRequest.date) }}
            </span>
            <span v-if="selectedRequest.due_date"
              class="flex items-center text-xs sm:text-sm md:text-base text-red-500">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-4 sm:w-4 mr-1" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {{ formatDate(selectedRequest.due_date) }}
            </span>
            <span v-if="selectedRequest.admin_name"
              class="flex items-center text-xs sm:text-sm md:text-base text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-4 sm:w-4 mr-1" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {{ selectedRequest.admin_name }}
            </span>
          </div>

          <!-- 내용 접기/펼치기 버튼 -->
          <div class="mb-4">
            <button @click="toggleContent"
              class="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md">
              <svg xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 sm:h-5 sm:w-5 mr-2 transform transition-transform duration-200"
                :class="{ 'rotate-180': !isContentExpanded }" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
              <span class="font-medium text-xs sm:text-sm md:text-base">{{ isContentExpanded ? '내용 접기' : '내용 펼치기'
              }}</span>
            </button>
          </div>

          <!-- 내용 영역 -->
          <div v-show="isContentExpanded" class="prose max-w-none text-xs sm:text-sm md:text-base lg:text-base"
            v-html="selectedRequest.content">
          </div>

          <!-- 제출 폼 -->
          <div v-if="['안읽음', '미제출', '보완요청'].includes(selectedRequest.status_name)" class="mt-6 border-t pt-6">
            <h3 class="text-sm sm:text-base md:text-lg font-semibold mb-4">제출하기</h3>
            <div class="space-y-4">
              <!-- 필수1 이미지 -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-xs sm:text-sm md:text-base font-medium text-gray-700 mb-1">필수1 이미지</label>
                  <div class="flex">
                    <div v-for="(image, index) in submitForm.required1" :key="'required1-' + index"
                      class="relative w-full aspect-square border rounded-md overflow-hidden bg-gray-50">
                      <img :src="image" class="w-full h-full object-contain">
                      <button @click="removeRequired1Image(index)"
                        class="absolute top-2 right-2 bg-red-500 text-white rounded-full w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 flex items-center justify-center text-base sm:text-lg md:text-xl">
                        ×
                      </button>
                    </div>
                    <div v-if="submitForm.required1.length < 1"
                      class="relative w-full aspect-square border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:border-blue-500 transition-colors duration-200"
                      @click="triggerRequired1ImageUpload">
                      <svg xmlns="http://www.w3.org/2000/svg"
                        class="h-10 w-10 sm:h-12 sm:w-12 md:h-14 md:w-14 text-gray-400" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                  </div>
                  <input type="file" ref="required1ImageInput" class="hidden" accept="image/*"
                    @change="handleRequired1ImageUpload">
                </div>
                <!-- 필수2 이미지 -->
                <div>
                  <label class="block text-xs sm:text-sm md:text-base font-medium text-gray-700 mb-1">필수2 이미지</label>
                  <div class="flex">
                    <div v-for="(image, index) in submitForm.required2" :key="'required2-' + index"
                      class="relative w-full aspect-square border rounded-md overflow-hidden bg-gray-50">
                      <img :src="image" class="w-full h-full object-contain">
                      <button @click="removeRequired2Image(index)"
                        class="absolute top-2 right-2 bg-red-500 text-white rounded-full w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 flex items-center justify-center text-base sm:text-lg md:text-xl">
                        ×
                      </button>
                    </div>
                    <div v-if="submitForm.required2.length < 1"
                      class="relative w-full aspect-square border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:border-blue-500 transition-colors duration-200"
                      @click="triggerRequired2ImageUpload">
                      <svg xmlns="http://www.w3.org/2000/svg"
                        class="h-10 w-10 sm:h-12 sm:w-12 md:h-14 md:w-14 text-gray-400" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                  </div>
                  <input type="file" ref="required2ImageInput" class="hidden" accept="image/*"
                    @change="handleRequired2ImageUpload">
                </div>
              </div>
              <!-- 추가 이미지 -->
              <div>
                <label class="block text-xs sm:text-sm md:text-base font-medium text-gray-700 mb-1">추가 이미지 (최대
                  5장)</label>
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div v-for="(image, index) in submitForm.images" :key="'additional-' + index"
                    class="relative w-full aspect-square border rounded-md overflow-hidden bg-gray-50">
                    <img :src="image" class="w-full h-full object-contain">
                    <button @click="removeImage(index)"
                      class="absolute top-2 right-2 bg-red-500 text-white rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-base sm:text-lg">
                      ×
                    </button>
                  </div>
                  <div v-if="submitForm.images.length < 5"
                    class="relative w-full aspect-square border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:border-blue-500 transition-colors duration-200"
                    @click="triggerImageUpload">
                    <div class="text-center">
                      <svg xmlns="http://www.w3.org/2000/svg"
                        class="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-2" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                      </svg>
                      <span class="text-sm text-gray-500">이미지 추가</span>
                    </div>
                  </div>
                </div>
                <input type="file" ref="imageInput" class="hidden" accept="image/*" multiple
                  @change="handleImageUpload">
              </div>
              <!-- 기타 텍스트 -->
              <div>
                <label class="block text-xs sm:text-sm md:text-base font-medium text-gray-700 mb-1">기타</label>
                <textarea v-model="submitForm.comment" rows="4"
                  class="w-full px-3 py-2 text-xs sm:text-sm md:text-base border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"></textarea>
              </div>
              <!-- 제출 버튼 -->
              <div class="flex justify-end">
                <button @click="submitVmdRequest"
                  class="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm md:text-base bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                  제출하기
                </button>
              </div>
            </div>
          </div>

          <!-- 하단 닫기 버튼 -->
          <div class="sticky bottom-0 left-0 right-0 bg-white border-t p-4 mt-6">
            <div class="flex flex-col sm:flex-row gap-3">
              <button v-if="!['미제출', '안읽음'].includes(selectedRequest.status_name)" @click="viewVmdSubmission"
                class="flex-1 bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors duration-200 flex items-center justify-center text-xs sm:text-sm md:text-base">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5 mr-2" fill="none"
                  viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                제출 내용 보기
              </button>
              <button @click="selectedRequest = null"
                class="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors duration-200 flex items-center justify-center text-xs sm:text-sm md:text-base">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5 mr-2" fill="none"
                  viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                닫기
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- VMD 제출 상세 보기 사이드 패널 -->
    <transition name="slide">
      <div v-if="selectedSubmission"
        class="fixed inset-y-0 right-0 w-full sm:w-[80%] md:w-[70%] lg:w-[60%] xl:w-[50%] 2xl:w-[45%] bg-white shadow-2xl overflow-y-auto z-50">
        <div class="p-4 sm:p-6">
          <!-- 헤더 -->
          <div class="flex justify-between items-center mb-4 sm:mb-6">
            <h2 class="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-800">{{ selectedSubmission.title
            }}
            </h2>
            <button @click="closeDetail" class="text-gray-500 hover:text-gray-700">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" fill="none"
                viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- 메타 정보 -->
          <div class="flex items-center space-x-4 mb-4 sm:mb-6">
            <span class="flex items-center text-xs sm:text-sm md:text-base text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-4 sm:w-4 mr-1" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              제출일: {{ formatDate(selectedSubmission.reg_date) }}
            </span>
            <span v-if="selectedSubmission.mod_date"
              class="flex items-center text-xs sm:text-sm md:text-base text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-4 sm:w-4 mr-1" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              수정일: {{ formatDate(selectedSubmission.mod_date) }}
            </span>
            <span :class="getStatusClass(selectedSubmission.status_code)"
              class="px-1.5 py-0.5 rounded-full text-[10px] sm:text-[11px] md:text-xs font-medium">
              {{ getStatusText(selectedSubmission.status_code) }}
            </span>
          </div>

          <!-- 담당자 평가 -->
          <div v-if="selectedSubmission.report_result_comment" class="mt-6">
            <h3 class="text-[13px] sm:text-[15px] font-semibold mb-2">담당자 평가</h3>
            <div class="bg-blue-50 p-4 rounded-lg border border-blue-100">
              <p class="text-gray-600 text-[11px] sm:text-xs whitespace-pre-line">{{
                selectedSubmission.report_result_comment }}</p>
            </div>
          </div>

          <!-- 코멘트 -->
          <div v-if="isEditing" class="mt-6">
            <h3 class="text-[13px] sm:text-[15px] font-semibold mb-2">코멘트 수정</h3>
            <textarea v-model="editForm.comment" rows="4"
              class="w-full px-3 py-2 text-[11px] sm:text-xs border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="코멘트를 입력해주세요"></textarea>
          </div>
          <div v-else-if="selectedSubmission.comment" class="mt-6">
            <h3 class="text-[13px] sm:text-[15px] font-semibold mb-2">코멘트</h3>
            <div class="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p class="text-gray-600 text-[11px] sm:text-xs whitespace-pre-line">{{ selectedSubmission.comment }}</p>
            </div>
          </div>

          <!-- 이미지 섹션 -->
          <div class="space-y-6 mt-6">
            <!-- 필수1 이미지 -->
            <div>
              <h3 class="text-[13px] sm:text-[15px] font-semibold mb-2">필수1 이미지</h3>
              <div v-if="isEditing" class="relative">
                <div v-if="editForm.required1" class="relative aspect-video">
                  <img :src="editForm.required1" class="w-full h-full object-contain rounded-lg border">
                  <button @click="removeEditImage('required1')"
                    class="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center">
                    ×
                  </button>
                </div>
                <div v-else
                  class="aspect-video border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-500"
                  @click="triggerEditImageUpload('required1')">
                  <div class="text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-gray-400 mx-auto mb-2" fill="none"
                      viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                    <span class="text-[11px] sm:text-xs text-gray-500">이미지 업로드</span>
                  </div>
                </div>
                <input type="file" ref="required1EditInput" class="hidden" accept="image/*"
                  @change="handleEditImageUpload('required1', $event)">
              </div>
              <div v-else class="relative aspect-video">
                <img v-if="selectedSubmission?.required1_url" :src="selectedSubmission.required1_url.url" loading="lazy"
                  class="w-full h-full object-contain rounded-lg border"
                  @click="openImageViewer(selectedSubmission.required1_url.url)">
              </div>
            </div>

            <!-- 필수2 이미지 -->
            <div>
              <h3 class="text-[13px] sm:text-[15px] font-semibold mb-2">필수2 이미지</h3>
              <div v-if="isEditing" class="relative">
                <div v-if="editForm.required2" class="relative aspect-video">
                  <img :src="editForm.required2" class="w-full h-full object-contain rounded-lg border">
                  <button @click="removeEditImage('required2')"
                    class="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center">
                    ×
                  </button>
                </div>
                <div v-else
                  class="aspect-video border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-500"
                  @click="triggerEditImageUpload('required2')">
                  <div class="text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-gray-400 mx-auto mb-2" fill="none"
                      viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                    <span class="text-[11px] sm:text-xs text-gray-500">이미지 업로드</span>
                  </div>
                </div>
                <input type="file" ref="required2EditInput" class="hidden" accept="image/*"
                  @change="handleEditImageUpload('required2', $event)">
              </div>
              <div v-else class="relative aspect-video">
                <img v-if="selectedSubmission?.required2_url" :src="selectedSubmission.required2_url.url" loading="lazy"
                  class="w-full h-full object-contain rounded-lg border"
                  @click="openImageViewer(selectedSubmission.required2_url.url)">
              </div>
            </div>

            <!-- 추가 이미지 -->
            <div v-if="selectedSubmission?.additional_urls?.length > 0 || isEditing">
              <h3 class="text-[13px] sm:text-[15px] font-semibold mb-2">추가 이미지 (최대 5장)</h3>
              <div v-if="isEditing" class="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div v-for="(url, index) in editForm.additional" :key="index" class="relative aspect-square">
                  <img :src="url" class="w-full h-full object-cover rounded-lg border">
                  <button @click="removeEditAdditionalImage(index)"
                    class="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center">
                    ×
                  </button>
                </div>
                <div v-if="editForm.additional.length < 5"
                  class="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-500"
                  @click="triggerEditAdditionalImageUpload">
                  <div class="text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-gray-400 mx-auto mb-2" fill="none"
                      viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                    <span class="text-[11px] sm:text-xs text-gray-500">이미지 추가</span>
                  </div>
                </div>
                <input type="file" ref="additionalEditInput" class="hidden" accept="image/*" multiple
                  @change="handleEditAdditionalImageUpload">
              </div>
              <div v-else class="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div v-for="(file, index) in selectedSubmission.additional_urls" :key="index"
                  class="relative aspect-square">
                  <img :src="file.url" class="w-full h-full object-cover rounded-lg border cursor-pointer"
                    @click="openImageViewer(file.url)">
                </div>
              </div>
            </div>
          </div>

          <!-- 하단 버튼 영역 -->
          <div class="sticky bottom-0 left-0 right-0 bg-white border-t p-4 mt-6">
            <div class="flex flex-col sm:flex-row gap-3">
              <!-- 연출공지 보기 버튼 추가 -->
              <button @click="viewRequest"
                class="flex-1 bg-purple-500 text-white py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg hover:bg-purple-600 transition-colors duration-200 flex items-center justify-center text-xs sm:text-sm md:text-base">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 mr-1.5 sm:mr-2"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                연출공지 보기
              </button>
              <!-- 담당자 승인 상태가 아닐 때만 수정 버튼 표시 -->
              <template v-if="selectedSubmission?.status_name !== '담당자승인'">
                <button v-if="!isEditing" @click="startEdit"
                  class="flex-1 bg-blue-500 text-white py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center justify-center text-xs sm:text-sm md:text-base">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 mr-1.5 sm:mr-2"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  수정하기
                </button>
                <template v-else>
                  <button @click="handleUpdate"
                    class="flex-1 bg-blue-500 text-white py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center justify-center text-xs sm:text-sm md:text-base">
                    저장
                  </button>
                  <button @click="cancelEdit"
                    class="flex-1 bg-gray-500 text-white py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg hover:bg-gray-600 transition-colors duration-200 flex items-center justify-center text-xs sm:text-sm md:text-base">
                    취소
                  </button>
                </template>
              </template>
              <button v-if="!isEditing" @click="closeDetail"
                class="flex-1 bg-gray-500 text-white py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg hover:bg-gray-600 transition-colors duration-200 flex items-center justify-center text-xs sm:text-sm md:text-base">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 mr-1.5 sm:mr-2"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                닫기
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- 이미지 뷰어 -->
    <transition name="fade">
      <div v-if="selectedImage" class="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center"
        @click="closeImageViewer">
        <img :src="selectedImage" class="max-w-full max-h-full object-contain p-4">
        <button class="absolute top-4 right-4 text-white hover:text-gray-300">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, shallowRef } from 'vue';
import noticeAPI from '@/api/notice/notice';
import vmdAPI from '@/api/vmd/vmd';
import Swal from 'sweetalert2';
import { resizeImage } from '@/utils/imageUtils';
import { useRouter } from 'vue-router';

// S3 기본 URL 환경변수
const S3_BASE_URL = process.env.VUE_APP_S3_URL;

// VMD 제출 상세 조회를 위한 상태
const selectedSubmission = ref(null);
const isEditing = ref(false);
const editForm = ref({
    comment: '',
    required1: null,
    required2: null,
    additional: [],
    year: new Date().getFullYear(),
    originalFiles: {
        required1: null,
        required2: null,
        additional: []
    }
});

const required1EditInput = ref(null);
const required2EditInput = ref(null);
const additionalEditInput = ref(null);

// 메모이제이션된 debounce 함수
const debounce = (fn, delay) => {
    let timeoutId;
    return (...args) => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
};

// window width 상태 관리 (디바운스 적용)
const windowWidth = ref(window.innerWidth);
const handleResize = debounce(() => {
    windowWidth.value = window.innerWidth;
}, 250);

// 날짜 포맷터 초기화 (메모이제이션)
const dateFormatter = new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
});

// 공지사항 관련 상태
const notices = shallowRef([]);
const totalNotices = ref(0);
const currentNoticePage = ref(1);
const itemsPerPage = 4;
const selectedNotice = shallowRef(null);
const isLoading = ref(false);

// 캐시 관련 상수
const CACHE_SIZE_LIMIT = 5; // 최대 캐시 페이지 수
const CACHE_EXPIRY_TIME = 5 * 60 * 1000; // 5분

// 캐시 데이터 구조
const noticeCache = new Map();

// 캐시 관리 함수
const manageCache = (page, data) => {
    // 캐시 크기 제한 확인
    if (noticeCache.size >= CACHE_SIZE_LIMIT) {
        // 가장 오래된 캐시 삭제
        const oldestKey = noticeCache.keys().next().value;
        noticeCache.delete(oldestKey);
    }

    // 캐시 데이터 저장 (타임스탬프 포함)
    noticeCache.set(page, {
        data,
        timestamp: Date.now()
    });
};

// 캐시 데이터 조회
const getCachedData = (page) => {
    const cached = noticeCache.get(page);
    if (!cached) return null;

    // 캐시 만료 확인
    if (Date.now() - cached.timestamp > CACHE_EXPIRY_TIME) {
        noticeCache.delete(page);
        return null;
    }

    return cached.data;
};

// 이미지 최적화 (메모이제이션)
const optimizeContent = (html) => {
    if (!html) return '';
    return html.replace(/<img/g, '<img loading="lazy"');
};

// 공지사항 조회 (캐싱 적용)
const fetchNotices = async (page = currentNoticePage.value) => {
    try {
        isLoading.value = true;
        const response = await noticeAPI.getNotices(page, itemsPerPage);
        notices.value = response.data.notices;
        totalNotices.value = response.data.total;
        
        // 데이터 캐싱 (서버에서 받은 읽음 상태 유지)
        manageCache(page, response.data.notices);
    } catch (error) {
        console.error('공지사항 조회 중 오류 발생:', error);
    } finally {
        isLoading.value = false;
    }
};

// 뒤로가기 처리 (디바운스 적용)
const handlePopState = debounce(() => {
    if (selectedImage.value) {
        closeImageViewer();
    } else if (selectedSubmission.value) {
        closeDetail();
    } else if (selectedNotice.value) {
        selectedNotice.value = null;
        history.replaceState(null, '', window.location.pathname);
    } else if (selectedRequest.value) {
        selectedRequest.value = null;
        history.replaceState(null, '', window.location.pathname);
    }
}, 100);

// 상세 보기 닫기 (최적화)
const closeDetail = () => {
    if (isEditing.value) {
        isEditing.value = false;
        initEditForm();
    }
    if (selectedSubmission.value) {
        selectedSubmission.value = null;
    }
    if (selectedNotice.value) {
        selectedNotice.value = null;
    }
    if (selectedRequest.value) {
        selectedRequest.value = null;
    }
    history.replaceState(null, '', window.location.pathname);
};

// 공지사항 상세 조회 (최적화)
const openDetail = async (notice) => {
    try {
        // 이미 읽은 공지사항인 경우 상세 조회만 실행
        if (notice.is_read) {
            const detailResponse = await noticeAPI.getNoticeDetail(notice.id);
            selectedNotice.value = {
                ...detailResponse.data,
                content: optimizeContent(detailResponse.data.content)
            };
        } else {
            // 읽지 않은 공지사항인 경우 병렬로 처리
            const [detailResponse] = await Promise.all([
                noticeAPI.getNoticeDetail(notice.id),
                noticeAPI.markAsRead(notice.id)
            ]);

            selectedNotice.value = {
                ...detailResponse.data,
                content: optimizeContent(detailResponse.data.content)
            };

            // 목록 업데이트 최적화
            notices.value = notices.value.map(n => {
                if (n.id === notice.id) {
                    return {
                        ...n,
                        is_read: true,
                        read_date: new Date().toISOString()
                    };
                }
                return n;
            });
            
            // 캐시된 데이터도 업데이트
            const currentPage = currentNoticePage.value;
            const cachedData = getCachedData(currentPage);
            if (cachedData) {
                const updatedNotices = cachedData.map(n => {
                    if (n.id === notice.id) {
                        return {
                            ...n,
                            is_read: true,
                            read_date: new Date().toISOString()
                        };
                    }
                    return n;
                });
                manageCache(currentPage, updatedNotices);
            }
        }
        
        // URL에 상세 조회 상태 추가
        history.pushState({ type: 'notice', id: notice.id }, '', `#notice-${notice.id}`);
    } catch (error) {
        console.error('공지사항 상세 조회 중 오류 발생:', error);
    }
};

// 페이지네이션 계산 (메모이제이션)
const totalNoticePages = computed(() => Math.ceil(totalNotices.value / itemsPerPage));
const paginatedNotices = computed(() => notices.value);

// 페이지 변경 시 공지사항 다시 조회 (캐싱 활용)
const handlePageChange = debounce((page) => {
    currentNoticePage.value = page;
    fetchNotices(page);
}, 100);

// 날짜 포맷 (메모이제이션)
const formatDate = (dateString) => {
    if (!dateString) return '';
    return dateFormatter.format(new Date(dateString));
};

// 2주 이내 게시물 체크 (메모이제이션)
const twoWeeksAgo = computed(() => {
    const date = new Date();
    date.setDate(date.getDate() - 10);
    return date;
});

const isNew = (date) => {
    if (!date) return false;
    return new Date(date) > twoWeeksAgo.value;
};

// 페이지네이션 visible 계산 (메모이제이션)
const getTotalVisible = computed(() => {
    const width = window.innerWidth;
    if (width >= 1024) return 7;
    if (width >= 768) return 5;
    return 5;
});

// VMD 요청사항 관련 상태
const vmdRequests = ref([]);
const totalVmdRequests = ref(0);
const currentRequestPage = ref(1);
const selectedRequest = ref(null);
const isVmdLoading = ref(false);
const isContentExpanded = ref(true);

// VMD 요청사항 조회
const fetchVmdRequests = async (page = currentRequestPage.value) => {
  try {
    isVmdLoading.value = true;
    const response = await vmdAPI.getVmdRequests(page, itemsPerPage);
    vmdRequests.value = response.data.requests;
    totalVmdRequests.value = response.data.total;
  } catch (error) {
    console.error('VMD 요청사항 조회 중 오류 발생:', error);
  } finally {
    isVmdLoading.value = false;
  }
};

// VMD 요청사항 상세 조회
const openVmdDetail = async (request) => {
    try {
        // 이미 상세 정보가 있는 경우 API 호출 스킵
        if (selectedRequest.value?.id === request.id) {
            return;
        }

        // 상세보기 먼저 열기
        selectedRequest.value = {
            ...request,
            content: '불러오는 중...' // 임시 컨텐츠
        };
        isContentExpanded.value = true;

        // 안읽음 상태 처리와 상세 정보 조회를 병렬로 처리
        const [detailResponse] = await Promise.all([
            vmdAPI.getVmdRequestDetail(request.id),
            request.status_name === '안읽음' ? updateVmdRequestStatus(request) : Promise.resolve()
        ]);

        // 상세 정보 업데이트
        selectedRequest.value = detailResponse.data;
        history.pushState({ type: 'request', id: request.id }, '', `#request-${request.id}`);
    } catch (error) {
        console.error('VMD 요청사항 상세 조회 중 오류 발생:', error);
        selectedRequest.value = null;
    }
};

// VMD 요청사항 상태 업데이트 함수 분리
const updateVmdRequestStatus = async (request) => {
    try {
        await vmdAPI.updateStatus(request.id, vmdAPI.VMD_STATUS.CHECKED);
        vmdRequests.value = vmdRequests.value.map(r => {
            if (r.id === request.id) {
                return {
                    ...r,
                    status_name: '미제출',
                    status_code: vmdAPI.VMD_STATUS.CHECKED
                };
            }
            return r;
        });
    } catch (error) {
        console.error('상태 업데이트 중 오류 발생:', error);
    }
};

// 제출 폼 관련 상태 - 필요할 때만 초기화하도록 함수로 분리
const initSubmitForm = () => {
    submitForm.value = {
        required1: [],
        required2: [],
        images: [],
        comment: '',
        year: new Date().getFullYear(),
        originalFiles: {
            required1: null,
            required2: null,
            additional: []
        }
    };
};

// 페이지네이션 계산
const totalRequestPages = computed(() => Math.ceil(totalVmdRequests.value / itemsPerPage));
const paginatedVmdRequests = computed(() => vmdRequests.value);

// 페이지 변경 시 VMD 요청사항 다시 조회
const handleVmdPageChange = debounce((page) => {
  currentRequestPage.value = page;
  fetchVmdRequests(page);
}, 100);

// VMD 제출 목록 관련 상태
const vmdSubmissions = ref([]);
const totalVmdSubmissions = ref(0);
const currentSubmissionPage = ref(1);
const isVmdSubmissionLoading = ref(false);

// 캐시 추가
const submissionsCache = ref(new Map());

// VMD 제출 목록 조회 (캐시 적용)
const fetchVmdSubmissions = async (page = currentSubmissionPage.value) => {
    try {
        // 캐시된 데이터가 있으면 사용
        if (submissionsCache.value.has(page)) {
            const cachedData = submissionsCache.value.get(page);
            vmdSubmissions.value = cachedData.submissions;
            totalVmdSubmissions.value = cachedData.total;
            return;
        }

        isVmdSubmissionLoading.value = true;
        const response = await vmdAPI.getVmdSubmissions(page, itemsPerPage);
        
        // S3 URL 구성
        const submissionsWithS3Url = response.data.submissions.map(submission => {
            if (submission.required1_url) {
                submission.required1_url = updateFileUrl(submission.required1_url);
            }
            if (submission.required2_url) {
                submission.required2_url = updateFileUrl(submission.required2_url);
            }
            if (submission.additional_urls && submission.additional_urls.length > 0) {
                submission.additional_urls = submission.additional_urls.map(file => 
                    updateFileUrl(file)
                );
            }
            return submission;
        });
        
        vmdSubmissions.value = submissionsWithS3Url;
        totalVmdSubmissions.value = response.data.total;

        // 캐시에 저장
        submissionsCache.value.set(page, {
            submissions: submissionsWithS3Url,
            total: response.data.total
        });

        // 캐시 크기 제한 (최대 5페이지)
        if (submissionsCache.value.size > 5) {
            const oldestKey = submissionsCache.value.keys().next().value;
            submissionsCache.value.delete(oldestKey);
        }
    } catch (error) {
        console.error('VMD 제출 목록 조회 중 오류 발생:', error);
    } finally {
        isVmdSubmissionLoading.value = false;
    }
};

// VMD 제출 목록 페이지네이션 계산
const totalSubmissionPages = computed(() => Math.ceil(totalVmdSubmissions.value / itemsPerPage));
const paginatedVmdSubmissions = computed(() => vmdSubmissions.value);

// VMD 제출 목록 페이지 변경 처리
const handleVmdSubmissionPageChange = debounce((page) => {
    currentSubmissionPage.value = page;
    fetchVmdSubmissions(page);
}, 100);

// VMD 제출 상세 보기
const openVmdSubmissionDetail = async (submission) => {
    try {
        // 상세 정보 조회
        const response = await vmdAPI.getVmdSubmissionDetail(submission.id);
        
        // S3 URL 구성
        if (response.data.required1_url) {
            response.data.required1_url = updateFileUrl(response.data.required1_url);
        }
        if (response.data.required2_url) {
            response.data.required2_url = updateFileUrl(response.data.required2_url);
        }
        if (response.data.additional_urls && response.data.additional_urls.length > 0) {
            response.data.additional_urls = response.data.additional_urls.map(file => 
                updateFileUrl(file)
            );
        }
        
        selectedSubmission.value = response.data;
        history.pushState({ type: 'submission', id: submission.id }, '', `#submission-${submission.id}`);
    } catch (error) {
        console.error('VMD 제출 상세 조회 중 오류 발생:', error);
    }
};

// S3 URL 구성 함수
const getS3Url = (filePath, uploadYear, isNew) => {
    if (!filePath) return null;
    if (isNew) {
        return `${S3_BASE_URL}/_static/images/vmd/${uploadYear}/${filePath}`;
    } else {
        return `${process.env.VUE_APP_BEFORE_API_URL}/images/reports/${filePath}`;
    }
};

// S3 URL로 객체 업데이트 함수
const updateFileUrl = (fileObj) => {
    if (!fileObj) return null;
    return {
        ...fileObj,
        url: getS3Url(fileObj.url, fileObj.upload_year, fileObj.is_new)
    };
};

// 상태에 따른 클래스 반환
const getStatusClass = (status_code) => {
    switch (status_code) {
        case 127: // 제출완료
            return 'bg-blue-100 text-blue-800';
        case 129: // 담당자승인
            return 'bg-green-100 text-green-800';
        case 128: // 보완요청
            return 'bg-red-100 text-red-800';
        case 126: // 미제출
            return 'bg-amber-100 text-amber-800';
        case 125: // 안읽음
            return 'bg-gray-100 text-gray-800';
        case 130: // 보완제출
            return 'bg-blue-100 text-blue-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
};

// 상태 코드에 따른 텍스트 반환 함수 추가
const getStatusText = (status_code) => {
    switch (status_code) {
        case 127:
            return '제출완료';
        case 129:
            return '담당자승인';
        case 128:
            return '보완요청';
        case 126:
            return '미제출';
        case 125:
            return '안읽음';
        case 130:
            return '보완제출';
        default:
            return '알 수 없음';
    }
};

// 초기 데이터 로드 (최적화)
onMounted(() => {
    fetchNotices();
    fetchVmdRequests();
    fetchVmdSubmissions();
    window.addEventListener('resize', handleResize);
    window.addEventListener('popstate', handlePopState);
    
    // URL 해시가 있는 경우 해당 항목 열기
    const hash = window.location.hash;
    if (hash) {
        if (hash.startsWith('#notice-')) {
            const noticeId = hash.replace('#notice-', '');
            const notice = notices.value.find(n => n.id === noticeId);
            if (notice) {
                openDetail(notice);
            }
        } else if (hash.startsWith('#request-')) {
            const requestId = hash.replace('#request-', '');
            const request = vmdRequests.value.find(r => r.id === requestId);
            if (request) {
                openVmdDetail(request);
            }
        }
    }
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('popstate', handlePopState);
});

// 제출 폼 관련 상태
const submitForm = ref({
  required1: [],
  required2: [],
  images: [],
  comment: '',
  year: new Date().getFullYear(),
  originalFiles: {
    required1: null,
    required2: null,
    additional: []
  }
})

const imageInput = ref(null)
const required1ImageInput = ref(null)
const required2ImageInput = ref(null)

const triggerImageUpload = () => {
  imageInput.value.click()
}

const triggerRequired1ImageUpload = () => {
  required1ImageInput.value.click()
}

const triggerRequired2ImageUpload = () => {
  required2ImageInput.value.click()
}

const handleImageUpload = async (event) => {
  const files = event.target.files
  for (let i = 0; i < files.length && submitForm.value.images.length < 5; i++) {
    const file = files[i]
    if (file.type.startsWith('image/')) {
      const resizedFile = await resizeImage(file)
      const reader = new FileReader()
      reader.onload = (e) => {
        submitForm.value.images.push(e.target.result)
        submitForm.value.originalFiles.additional.push(file)
      }
      reader.readAsDataURL(resizedFile)
    }
  }
}

const handleRequired1ImageUpload = async (event) => {
  const file = event.target.files[0]
    if (file && file.type.startsWith('image/')) {
    const resizedFile = await resizeImage(file)
    const reader = new FileReader()
        reader.onload = (e) => {
      submitForm.value.required1 = [e.target.result]
      submitForm.value.originalFiles.required1 = file
    }
    reader.readAsDataURL(resizedFile)
  }
}

const handleRequired2ImageUpload = async (event) => {
  const file = event.target.files[0]
  if (file && file.type.startsWith('image/')) {
    const resizedFile = await resizeImage(file)
    const reader = new FileReader()
            reader.onload = (e) => {
      submitForm.value.required2 = [e.target.result]
      submitForm.value.originalFiles.required2 = file
    }
    reader.readAsDataURL(resizedFile)
  }
}

const removeImage = (index) => {
  submitForm.value.images.splice(index, 1)
  submitForm.value.originalFiles.additional.splice(index, 1)
}

const removeRequired1Image = (index) => {
  submitForm.value.required1.splice(index, 1)
  submitForm.value.originalFiles.required1 = null
}

const removeRequired2Image = (index) => {
  submitForm.value.required2.splice(index, 1)
  submitForm.value.originalFiles.required2 = null
}

const submitVmdRequest = async () => {
    try {
        if (submitForm.value.required1.length === 0 || submitForm.value.required2.length === 0) {
            Swal.fire({
                icon: 'error',
                title: '오류',
                text: '필수 이미지를 모두 업로드해주세요.'
            });
            return;
        }

        // 제출 확인
        const confirmed = await Swal.fire({
            icon: 'warning',
            title: selectedRequest.value.status_name === '보완요청' ? '수정하시겠습니까?' : '제출하면 수정할 수 없습니다',
            text: selectedRequest.value.status_name === '보완요청' ? '수정된 내용으로 다시 제출됩니다.' : '제출하시겠습니까?',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: '확인',
            cancelButtonText: '취소',
            reverseButtons: true
        });

        if (confirmed.isConfirmed) {
            // 로딩 표시
            Swal.fire({
                title: '업로드중입니다',
                timerProgressBar: true,
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            // FormData 생성
            const formData = await createFormData();

            // 보완요청인 경우 수정 API 호출, 아닌 경우 제출 API 호출
            if (selectedRequest.value.status_name === '보완요청') {
                await vmdAPI.updateReport(selectedRequest.value.id, formData);
            } else {
                await vmdAPI.submitReport(selectedRequest.value.id, formData);
            }

            // 상태 업데이트 (제출완료)
            await vmdAPI.updateStatus(selectedRequest.value.id, vmdAPI.VMD_STATUS.SUBMITTED);

            // 목록 업데이트
            updateVmdRequestList(selectedRequest.value.id);

            // 성공 메시지
            await Swal.fire({
                icon: 'success',
                title: '성공',
                text: selectedRequest.value.status_name === '보완요청' ? '수정이 완료되었습니다.' : '제출이 완료되었습니다.'
            });

            // 폼 초기화 및 상세보기 닫기
            selectedRequest.value = null;
            initSubmitForm();
        }
    } catch (error) {
        console.error('VMD 요청사항 제출 중 오류 발생:', error);
        Swal.fire({
            icon: 'error',
            title: '오류',
            text: error.response?.data?.error || '제출 중 오류가 발생했습니다.'
        });
    }
};

// FormData 생성 함수 분리
const createFormData = async () => {
    const formData = new FormData();
    formData.append('comment', submitForm.value.comment);
    formData.append('year', String(submitForm.value.year));  // 연도를 문자열로 변환하여 추가

    // 필수 이미지 추가
    const [required1Blob, required2Blob] = await Promise.all([
        fetch(submitForm.value.required1[0]).then(r => r.blob()),
        fetch(submitForm.value.required2[0]).then(r => r.blob())
    ]);

    formData.append('pictures', required1Blob, submitForm.value.originalFiles.required1.name);
    formData.append('pictures', required2Blob, submitForm.value.originalFiles.required2.name);

    // 추가 이미지 병렬 처리
    const additionalBlobs = await Promise.all(
        submitForm.value.images.map((image, i) => 
            fetch(image).then(r => r.blob())
                .then(blob => ({ blob, name: submitForm.value.originalFiles.additional[i].name }))
        )
    );

    additionalBlobs.forEach(({ blob, name }) => {
        formData.append('pictures', blob, name);
    });

    return formData;
};

// VMD 요청사항 목록 업데이트 함수 분리
const updateVmdRequestList = (requestId) => {
    vmdRequests.value = vmdRequests.value.map(r => {
        if (r.id === requestId) {
            return {
                ...r,
                status_name: '제출완료',
                status_code: vmdAPI.VMD_STATUS.SUBMITTED
            };
        }
        return r;
    });
};

const toggleContent = () => {
  isContentExpanded.value = !isContentExpanded.value
}

// 첨부파일 다운로드 함수 수정
const downloadAttachment = (attachment) => {
  if (!attachment?.file_name) return;
  
  const defaultUrl = "https://work-api.agabang.com:6300";
  const url = `${defaultUrl}/api/vmd_service/notice/getAttachmentDownload?file_name=${attachment.file_name}`;
  
  // 다운로드 링크 생성 및 클릭
  const link = document.createElement('a');
  link.href = url;
  link.download = attachment.original_name || attachment.file_name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const formatDate_mmdd = (dateString) => {
    if (!dateString) return '';
    return new Intl.DateTimeFormat('ko-KR', {
        month: 'numeric',
        day: 'numeric'
    }).format(new Date(dateString));
};

// editForm 초기화 함수
const initEditForm = () => {
  editForm.value = {
    comment: '',
    required1: null,
    required2: null,
    additional: [],
    year: new Date().getFullYear(),
    originalFiles: {
      required1: null,
      required2: null,
      additional: []
    }
  };
};

// 수정 모드 시작
const startEdit = () => {
  isEditing.value = true;
  editForm.value = {
    comment: selectedSubmission.value.comment || '',
    required1: selectedSubmission.value.required1_url?.url || null,
    required2: selectedSubmission.value.required2_url?.url || null,
    additional: selectedSubmission.value.additional_urls?.map(file => file.url) || [],
    year: new Date().getFullYear(),
    originalFiles: {
      required1: null,
      required2: null,
      additional: []
    }
  };
};

// 수정 취소
const cancelEdit = () => {
  isEditing.value = false;
  initEditForm();
};

// 이미지 업로드 트리거
const triggerEditImageUpload = (type) => {
    if (type === 'required1') {
        required1EditInput.value.click();
    } else if (type === 'required2') {
        required2EditInput.value.click();
    }
};

const triggerEditAdditionalImageUpload = () => {
    additionalEditInput.value.click();
};

// 이미지 업로드 처리
const handleEditImageUpload = async (type, event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
        const resizedFile = await resizeImage(file);
        const reader = new FileReader();
        reader.onload = (e) => {
            editForm.value[type] = e.target.result;
            editForm.value.originalFiles[type] = resizedFile;
        };
        reader.readAsDataURL(resizedFile);
    }
};

// 추가 이미지 업로드 처리
const handleEditAdditionalImageUpload = async (event) => {
    const files = event.target.files;
    const remainingSlots = 5 - editForm.value.additional.length;
    
    for (let i = 0; i < files.length && i < remainingSlots; i++) {
        const file = files[i];
        if (file.type.startsWith('image/')) {
            const resizedFile = await resizeImage(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                editForm.value.additional.push(e.target.result);
                editForm.value.originalFiles.additional.push(resizedFile);
            };
            reader.readAsDataURL(resizedFile);
        }
    }
    
    // 5장을 초과하는 경우 경고 메시지 표시
    if (files.length > remainingSlots) {
        Swal.fire({
            icon: 'warning',
            title: '알림',
            text: `추가 이미지는 최대 5장까지만 업로드할 수 있습니다. (현재 ${editForm.value.additional.length}장)`
        });
    }
};

// 이미지 제거
const removeEditImage = (type) => {
    editForm.value[type] = null;
    editForm.value.originalFiles[type] = null;
};

const removeEditAdditionalImage = (index) => {
    editForm.value.additional.splice(index, 1);
    editForm.value.originalFiles.additional.splice(index, 1);
};

// 수정 저장
const handleUpdate = async () => {
    try {
        // 필수 이미지 검증
        const hasRequired1 = editForm.value.required1 || editForm.value.originalFiles.required1;
        const hasRequired2 = editForm.value.required2 || editForm.value.originalFiles.required2;

        if (!hasRequired1 || !hasRequired2) {
            Swal.fire({
                icon: 'error',
                title: '오류',
                text: '필수 이미지 2장을 모두 업로드해주세요.'
            });
            return;
        }

        // 제출 확인
        const confirmed = await Swal.fire({
            icon: 'warning',
            title: '수정하시겠습니까?',
            text: '수정된 내용으로 다시 제출됩니다.',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: '확인',
            cancelButtonText: '취소',
            reverseButtons: true
        });

        if (confirmed.isConfirmed) {
            // FormData 생성
            const formData = new FormData();
            let hasChanges = false;

            // 코멘트가 수정된 경우에만 전송
            if (editForm.value.comment !== selectedSubmission.value.comment) {
                formData.append('comment', editForm.value.comment);
                hasChanges = true;
            }

            // 연도 정보 추가
            formData.append('year', editForm.value.year);

            // 이미지 변경 확인 (삭제, 수정, 추가 모두 포함)
            const modifiedTypes = [];
            
            // 필수1 이미지 변경 확인
            if (editForm.value.required1 !== selectedSubmission.value.required1_url?.url) {
                modifiedTypes.push('0');
                if (editForm.value.originalFiles.required1) {
                    formData.append('pictures', editForm.value.originalFiles.required1);
                }
                hasChanges = true;
            }

            // 필수2 이미지 변경 확인
            if (editForm.value.required2 !== selectedSubmission.value.required2_url?.url) {
                modifiedTypes.push('1');
                if (editForm.value.originalFiles.required2) {
                    formData.append('pictures', editForm.value.originalFiles.required2);
                }
                hasChanges = true;
            }

            // 추가 이미지 변경 확인
            const originalAdditionalUrls = selectedSubmission.value.additional_urls?.map(file => file.url) || [];
            const currentAdditionalUrls = editForm.value.additional.filter(url => url.startsWith('http'));
            
            // 새로 추가된 이미지 처리
            if (editForm.value.originalFiles.additional.length > 0) {
                editForm.value.originalFiles.additional.forEach(file => {
                    formData.append('pictures', file);
                    modifiedTypes.push('2');
                });
                hasChanges = true;
            }

            // 삭제된 이미지 처리
            const deletedUrls = originalAdditionalUrls.filter(url => !currentAdditionalUrls.includes(url));
            deletedUrls.forEach(url => {
                const fileToDelete = selectedSubmission.value.additional_urls.find(file => file.url === url);
                if (fileToDelete) {
                    modifiedTypes.push(`delete_${fileToDelete.id}`);
                }
            });
            if (deletedUrls.length > 0) {
                hasChanges = true;
            }

            if (hasChanges) {
                formData.append('modified_types', JSON.stringify(modifiedTypes));
                
                await vmdAPI.updateReport(selectedSubmission.value.report_id, formData);
                
                // 보완요청 상태였다면 보완제출로, 그 외에는 제출완료로 상태 변경
                const newStatusCode = selectedSubmission.value.status_code === vmdAPI.VMD_STATUS.REQUEST
                    ? vmdAPI.VMD_STATUS.RESUBMITTED
                    : vmdAPI.VMD_STATUS.SUBMITTED;
                
                await vmdAPI.updateStatus(selectedSubmission.value.id, newStatusCode);
                
                // 성공 메시지
                await Swal.fire({
                    icon: 'success',
                    title: '성공',
                    text: '수정이 완료되었습니다.'
                });

                // 수정 모드 종료
                isEditing.value = false;

                // 상세 정보 다시 불러오기
                const response = await vmdAPI.getVmdSubmissionDetail(selectedSubmission.value.id);
                
                // S3 URL 구성
                if (response.data.required1_url) {
                    response.data.required1_url = updateFileUrl(response.data.required1_url);
                }
                if (response.data.required2_url) {
                    response.data.required2_url = updateFileUrl(response.data.required2_url);
                }
                if (response.data.additional_urls && response.data.additional_urls.length > 0) {
                    response.data.additional_urls = response.data.additional_urls.map(file => 
                        updateFileUrl(file)
                    );
                }
                
                selectedSubmission.value = response.data;
                
                // 목록의 코멘트와 상태도 업데이트
                const submissionIndex = vmdSubmissions.value.findIndex(s => s.id === selectedSubmission.value.id);
                if (submissionIndex !== -1) {
                    vmdSubmissions.value[submissionIndex] = {
                        ...vmdSubmissions.value[submissionIndex],
                        comment: response.data.comment,
                        status_code: newStatusCode,
                        status_name: getStatusName(newStatusCode)
                    };
                }
            } else {
                Swal.fire({
                    icon: 'info',
                    title: '알림',
                    text: '변경된 내용이 없습니다.'
                });
            }
        }
    } catch (error) {
        console.error('VMD 제출 수정 중 오류 발생:', error);
        Swal.fire({
            icon: 'error',
            title: '오류',
            text: error.response?.data?.error || '수정 중 오류가 발생했습니다.'
        });
    }
};

// 상태 이름 반환 함수 추가
const getStatusName = (status_code) => {
  switch (status_code) {
    case vmdAPI.VMD_STATUS.SUBMITTED:
    case vmdAPI.VMD_STATUS.RESUBMITTED:
      return '제출완료';
    case vmdAPI.VMD_STATUS.APPROVED:
      return '담당자승인';
    case vmdAPI.VMD_STATUS.REQUEST:
      return '보완요청';
    default:
      return '알 수 없음';
  }
};

// 이미지 뷰어 닫기
const closeImageViewer = () => {
    selectedImage.value = null;
};

// 이미지 뷰어 열기
const openImageViewer = (url) => {
    selectedImage.value = url;
};

// 선택된 이미지 저장
const selectedImage = ref(null);

// 뒤로가기 처리
// const handlePopState = () => {
//     if (selectedImage.value) {
//         closeImageViewer();
//     } else if (selectedSubmission.value) {
//         closeDetail();
//     } else if (selectedNotice.value) {
//         selectedNotice.value = null;
//         history.replaceState(null, '', window.location.pathname);
//     } else if (selectedRequest.value) {
//         selectedRequest.value = null;
//         history.replaceState(null, '', window.location.pathname);
//     }
// };

const router = useRouter();

// 연출공지 보기 함수 추가
const viewRequest = () => {
    if (selectedSubmission.value) {
        const requestId = selectedSubmission.value.id;
        // 현재 상세보기 닫기
        selectedSubmission.value = null;
        // VMD 요청사항 페이지로 이동
        router.push({
            name: 'VmdRequests',
            hash: `#request-${requestId}`
        });
    }
};

const viewVmdSubmission = () => {
    if (selectedRequest.value) {
        const requestId = selectedRequest.value.id;
        // 현재 상세보기 닫기
        selectedRequest.value = null;
        // VMD 제출 목록 페이지로 이동 (request_id를 그대로 사용)
        router.push({
            name: 'VmdSubmissions',
            hash: `#submission-${requestId}`
        });
    }
};
</script>
<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

:deep(.prose) {
  max-width: none;
  color: #374151;
}

:deep(.prose p) {
  margin-top: 1.25em;
  margin-bottom: 1.25em;
}

/* 페이지네이션 반응형 텍스트 크기 */
/* :deep(.pagination-text) {
  font-size: 10px;
}

:deep(.pagination-text .v-btn) {
  font-size: 10px;
} */

@media (min-width: 640px) {
  :deep(.pagination-text) {
    font-size: 12px;
  }

  :deep(.pagination-text .v-btn) {
    font-size: 12px;
  }
}

@media (min-width: 1024px) {
  :deep(.pagination-text) {
    font-size: 14px;
  }

  :deep(.pagination-text .v-btn) {
    font-size: 14px;
  }
}

@media (min-width: 1280px) {
  :deep(.pagination-text) {
    font-size: 15px;
  }

  :deep(.pagination-text .v-btn) {
    font-size: 15px;
  }
}
</style>
